import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Step4Props {
  onNext: (data: Step4Data) => void;
  onBack: () => void;
  initialData?: Step4Data;
}

export interface Step4Data {
  designStyle: string;
  colorPreference?: string;
  referenceWebsites?: string;
  brandingNotes?: string;
}

const designStyles = [
  { value: 'modern', label: 'Modern/Minimal', description: 'Clean, simple, elegant' },
  { value: 'corporate', label: 'Corporate', description: 'Professional, trustworthy' },
  { value: 'playful', label: 'Playful', description: 'Fun, colorful, creative' },
  { value: 'dark', label: 'Dark Mode', description: 'Dark theme throughout' },
];

export const Step4Design = ({ onNext, onBack, initialData }: Step4Props) => {
  const [designStyle, setDesignStyle] = useState(initialData?.designStyle || '');
  const [colorPreference, setColorPreference] = useState(initialData?.colorPreference || '');
  const [referenceWebsites, setReferenceWebsites] = useState(initialData?.referenceWebsites || '');
  const [brandingNotes, setBrandingNotes] = useState(initialData?.brandingNotes || '');

  const handleSubmit = () => {
    if (designStyle) {
      onNext({ designStyle, colorPreference, referenceWebsites, brandingNotes });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Design Preferences</h2>
        <p className="text-gray-600 mt-2">Tell us about your visual preferences</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Design Style *</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {designStyles.map((style) => (
            <div
              key={style.value}
              onClick={() => setDesignStyle(style.value)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                designStyle === style.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <h4 className="font-semibold text-gray-900">{style.label}</h4>
              <p className="text-sm text-gray-600">{style.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-2">
          Color Preference (Optional)
        </label>
        <Input
          id="color"
          type="text"
          value={colorPreference}
          onChange={(e) => setColorPreference(e.target.value)}
          placeholder="e.g., Blue and white, Corporate red, Green/natural tones"
        />
      </div>

      <div>
        <label htmlFor="references" className="block text-sm font-medium text-gray-700 mb-2">
          Reference Websites (Optional)
        </label>
        <textarea
          id="references"
          value={referenceWebsites}
          onChange={(e) => setReferenceWebsites(e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Share URLs of websites you like (one per line)"
        />
      </div>

      <div>
        <label htmlFor="branding" className="block text-sm font-medium text-gray-700 mb-2">
          Branding Notes (Optional)
        </label>
        <textarea
          id="branding"
          value={brandingNotes}
          onChange={(e) => setBrandingNotes(e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Any specific branding requirements, logo details, etc."
        />
      </div>

      <div className="flex justify-between">
        <Button onClick={onBack} variant="outline" className="px-8">
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!designStyle}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
